import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import ResetPasswordForm from "../components/ResetPasswordForm"
import { LoginAd } from "@/modules/auth/login/ui/components/LoginAd"
import { ResetPasswordAsync } from "@/modules/auth/infrastructure/auth-service"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { CheckCircle2 } from "lucide-react"
import { AxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)

  const email = searchParams.get("email") || ""
  const token = searchParams.get("token") || ""
  const { getToken } = useRecaptcha()

  useEffect(() => {
    if (!email || !token) {
      setErrorMessage(
        "Invalid reset link. Please request a new password reset."
      )
    }
  }, [email, token])

  useEffect(() => {
    if (!cooldownUntil) return
    const tick = () => {
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(null)
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [cooldownUntil])

  const isCoolingDown = cooldownUntil !== null && Date.now() < cooldownUntil

  const handleSubmit = async (newPassword: string) => {
    if (!email || !token) {
      setErrorMessage(
        "Invalid reset link. Please request a new password reset."
      )
      return
    }

    setIsLoading(true)
    setErrorMessage("")

    try {
      const captchaToken = await getToken("reset_password")
      await ResetPasswordAsync({ email, token, newPassword, captchaToken })
      setIsSuccess(true)
      setTimeout(() => {
        navigate("/login")
      }, 3000)
    } catch (err: unknown) {
      const axiosErr = err as AxiosError
      if (axiosErr?.response?.status === HttpStatusCodes.TOO_MANY_REQUESTS) {
        const retryAfter = parseInt(
          (axiosErr.response.headers as Record<string, string>)?.['retry-after'] ?? '60',
          10
        )
        setCooldownUntil(Date.now() + retryAfter * 1000)
        return
      }
      const message =
        err instanceof Error
          ? err.message
          : "Failed to reset password. Please try again or request a new reset link."
      setErrorMessage(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col md:flex-row w-full min-h-screen">
      <LoginAd />
      <div className="flex-1 flex items-center justify-start">
        <div className="w-full max-w-full pr-4 md:pl-30">
          {isSuccess ? (
            <div className="w-full md:w-[75%] justify-center items-center">
              <Card>
                <CardHeader className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <CardTitle className="text-2xl">
                    Password reset successful
                  </CardTitle>
                  <CardDescription className="text-base">
                    Your password has been successfully reset. You will be
                    redirected to the login page in a few seconds.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <Button
                    onClick={() => navigate("/login")}
                    className="bg-[#1A237E] hover:bg-[#1A237E]/90"
                  >
                    Go to login
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            <ResetPasswordForm
              onSubmit={handleSubmit}
              isLoading={isLoading}
              errorMessage={errorMessage}
              hasValidToken={!!email && !!token}
              isCoolingDown={isCoolingDown}
              email={email}
            />
          )}
        </div>
      </div>
    </div>
  )
}
